/**
 * Mirror of the backend's `src/lib/admin-permissions.ts`.
 *
 * Keep in sync by hand when the backend adds constants. Drift here is
 * UX-only: the server enforces every permission via requirePermission() —
 * these values only decide what the UI renders.
 */
export const PERMISSIONS = {
  // Money movement
  WALLET_ADD_BALANCE: "wallet.add_balance",
  WALLET_REFUND: "wallet.refund",
  WALLET_ADJUSTMENT: "wallet.adjustment",
  WALLET_INCREASE_FLOAT: "wallet.increase_float",
  WALLET_WITHDRAW: "wallet.withdraw",
  FUNDS_COINFLOW: "funds.coinflow",
  FUNDS_STRADDLE: "funds.straddle",
  CRYPTO_MANUAL_WITHDRAWAL: "crypto.manual_withdrawal",
  SWEEP_TO_LEDGER: "sweep_to_ledger",
  EXCHANGE_WRITE: "exchange.write",
  // Run a rail's reconciliation poll on demand (stale-payments page) —
  // superadmin only
  RAIL_RECONCILE: "rail.reconcile",

  // Fraud / KYC overrides (sensitive — superadmin only)
  FRAUD_REVIEW_DECIDE: "fraud_review.decide",
  PERSONAL_THAIQR_DECIDE: "personal_thaiqr.decide",
  CRYPTO_DEPOSIT_DECIDE: "crypto_deposit.decide",
  // Approve/decline crypto withdrawals held by the per-transfer screening
  CRYPTO_WITHDRAWAL_DECIDE: "crypto_withdrawal.decide",

  // Support-safe operational helpers
  FRAUD_REVIEW_ACK: "fraud_review.ack",
  CARD_VERIFICATION_REVIEW: "card_verification.review",
  // KYC review queue (list + raw provider payload) — support + superadmin
  KYC_REVIEW: "kyc.review",
  KYC_RETRY: "kyc.retry",
  KYC_DECLINE: "kyc.decline",
  // Flip is_verified ON for a customer whose KYC session already passed
  // (support + superadmin; server refuses it without a `success` session)
  KYC_MARK_VERIFIED: "kyc.mark_verified",
  // Mark a phone number verified without an OTP (support + superadmin;
  // reason required, posted to #cust-support)
  PHONE_VERIFICATION_SKIP: "phone_verification.skip",
  KYC_VERIFY_BANK: "kyc.verify_bank",
  KYC_AIPRISE_CREATE: "kyc.aiprise_create",
  KYC_PERSONA_SIMULATE: "kyc.persona_simulate",
  STABLES_DEPOSIT_SIMULATE: "stables.deposit_simulate",
  SCREEN_WALLET: "screen.wallet",
  REACTIVATE_PAYMENT_METHOD: "payment_method.reactivate",
  USER_REACTIVATE: "user.reactivate",
  USER_ACCOUNT_EXISTS_NOTIFY: "user.account_exists_notify",
  // Pause/resume automatic fraud scoring for a customer (support + superadmin;
  // support is capped at 24h server-side)
  FRAUD_WHITELIST_WRITE: "fraud_whitelist.write",

  // User moderation
  USER_BLOCK: "user.block",
  USER_DEACTIVATE: "user.deactivate",
  // Permanent delete of an account with no deposit/payment/withdrawal activity
  USER_DELETE: "user.delete",
  USER_SETTINGS_UPDATE: "user_settings.update",
  USER_SETTINGS_UPDATE_SENSITIVE: "user_settings.update_sensitive",
  PAYMENT_LIMITS_UPDATE: "payment_limits.update",

  // User notes
  USER_NOTES_READ: "user_notes.read",
  USER_NOTES_WRITE: "user_notes.write",

  // Config mutations (superadmin only)
  FEATURE_FLAGS_WRITE: "feature_flags.write",
  BLOCKED_MERCHANTS_WRITE: "blocked_merchants.write",
  CRYPTO_SENDER_WHITELIST_WRITE: "crypto_sender_whitelist.write",
  // The minimum balance the master wallet must retain per token
  MASTER_WALLET_SETTINGS_WRITE: "master_wallet.settings_write",

  // Reads
  USERS_READ: "users.read",
  // The per-customer revenue tile on the user page (superadmin only; the
  // server nulls stats.totalRevenueUsd for everyone else).
  USER_REVENUE_READ: "users.revenue_read",
  LOOKUPS_READ: "lookups.read",
  // Raw bank account / routing numbers and raw provider payloads inside the
  // user-lookup reads. The server strips them without it, so the columns and
  // the response-data dialog are hidden rather than shown empty.
  LOOKUPS_READ_SENSITIVE: "lookups.read_sensitive",
  FEATURE_FLAGS_READ: "feature_flags.read",
  BLOCKED_MERCHANTS_READ: "blocked_merchants.read",
  FRAUD_WHITELIST_READ: "fraud_whitelist.read",
  CRYPTO_DEPOSIT_READ: "crypto_deposit.read",
  CRYPTO_SENDER_WHITELIST_READ: "crypto_sender_whitelist.read",
  CRYPTO_WITHDRAWAL_READ: "crypto_withdrawal.read",
  // Privy master wallet balances + treasury minimums (live on-chain read)
  MASTER_WALLET_READ: "master_wallet.read",
  // The Fraud page (alerts, payment scores, card reviews, summary tiles) —
  // superadmin, readonly and support; every write on it has its own gate.
  FRAUD_READ: "fraud.read",
  // Merchant pages (transaction history, payers, summary) — same roles.
  MERCHANTS_READ: "merchants.read",

  // Metrics. METRICS_READ gates the metrics page itself; the sensitive
  // metrics and KPI tiles (revenue, provider-level volumes, corridors,
  // customer float, fraud) additionally need METRICS_FINANCIAL_READ.
  METRICS_READ: "metrics.read",
  METRICS_FINANCIAL_READ: "metrics.financial_read",
  // Support-load page (/admin/support-load/*): review-queue backlog,
  // time-to-resolve, Chatwoot. Support + readonly + superadmin, not marketing.
  SUPPORT_LOAD_READ: "support_load.read",
  // Live page (/admin/live/health): per-rail health from Datadog logs.
  // Superadmin only.
  LIVE_HEALTH_READ: "live_health.read",

  // Admin user management (superadmin only)
  ADMIN_USERS_MANAGE: "admin_users.manage",
  // Support spending caps: audit page (read) + per-admin overrides (write),
  // superadmin only
  ADMIN_SPENDING_READ: "admin_spending.read",
  ADMIN_SPENDING_LIMITS_WRITE: "admin_spending.limits_write",

  // Creator Rewards
  CREATOR_REWARDS_READ: "creator_rewards.read",
  CREATOR_REWARDS_WRITE: "creator_rewards.write",

  // Announcements: Home cards (/admin/home-announcements) and inbox messages
  // (/admin/announcements). Marketing + superadmin; readonly can read.
  ANNOUNCEMENTS_READ: "announcements.read",
  ANNOUNCEMENTS_WRITE: "announcements.write",

  // AI chatbot (read-only production DB queries) — superadmin only
  AI_CHATBOT: "ai_chatbot.use",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ADMIN_ROLES = [
  "superadmin",
  "support",
  "readonly",
  "marketing",
] as const;
export type Role = (typeof ADMIN_ROLES)[number];

export const isRole = (value: string): value is Role =>
  (ADMIN_ROLES as readonly string[]).includes(value);
